import React from "react";
import { Typography, Box, Container, Paper, Button, List, ListItem, ListItemIcon, ListItemText, Grid, Card, CardContent, CardActions } from "@mui/material";
import { 
  Brightness1 as BulletIcon,
  ArrowForward as ArrowForwardIcon
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import PracticeAreasGrid from "../components/PracticeAreasGrid";
import SeoMetaTags from "../components/SeoMetaTags";

// Styled components
const HeroSection = styled(Box)(({ theme }) => {
  // Get the theme URL dynamically
  const themeUrl = window.wpData?.themeUrl || '';
  
  return {
    backgroundImage: `linear-gradient(rgba(14, 49, 88, 0.85), rgba(14, 49, 88, 0.85)), url('${themeUrl}/build/grey-building.jpg')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    color: 'white',
    padding: theme.spacing(12, 0),
    marginBottom: theme.spacing(6),
  };
});

const HeroTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '2.8rem',
  marginBottom: theme.spacing(2),
  maxWidth: '700px',
}));

const HeroSubtitle = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  marginBottom: theme.spacing(4),
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#f0b94d',
  color: '#0e3158',
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  marginRight: theme.spacing(2),
  '&:hover': {
    backgroundColor: '#d9a843',
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  borderColor: 'white',
  color: 'white',
  padding: theme.spacing(1.5, 3),
  '&:hover': {
    borderColor: '#f0b94d',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1.75rem",
  marginBottom: theme.spacing(2),
  color: "#0e3158", // Using the blue color from the theme
  position: "relative",
  paddingBottom: theme.spacing(1),
  textAlign: "center",
  "&:after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "60px",
    height: "3px",
    backgroundColor: "#f0b94d", // Using the gold color from the theme
  },
}));

const ContentPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  borderRadius: "8px",
  boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
}));

const Highlight = styled("span")(({ theme }) => ({
  fontWeight: 700,
  color: "#0e3158", // Using the blue color to highlight text
}));

const CTASection = styled(Box)(({ theme }) => ({
  backgroundColor: '#0e3158',
  color: 'white',
  padding: theme.spacing(6, 0),
  textAlign: 'center',
}));

function HomePage() {
  return (
    <>
      {/* SEO Meta Tags */}
      <SeoMetaTags 
        slug="home" 
        defaultTitle="Sentry Law - Calgary Law Firm | Legal Services"
        defaultDescription="Sentry Law is a Calgary-based law firm dedicated to protecting your interests with skill, integrity, and compassion. Contact us for a free consultation."
      />
      
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <HeroTitle variant="h1">
            Standing Guard for Your Legal Needs
          </HeroTitle>

          <Box>
            <PrimaryButton 
              variant="contained" 
              component={RouterLink} 
              to="/contact-us"
            >
              Schedule Free Consultation
            </PrimaryButton>
            <SecondaryButton 
              variant="outlined" 
              component={RouterLink} 
              to="/practice-areas"
            >
              Learn More
            </SecondaryButton>
          </Box>
        </Container>
      </HeroSection>

      {/* Intro Section */}
      <Container maxWidth="lg">
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <SectionTitle variant="h2">
            Dedicated to Protecting Your Rights and Interests
          </SectionTitle>

          <Typography variant="body1" sx={{ my: 3, maxWidth: '800px', mx: 'auto', lineHeight: 1.7 }}>
            Sentry Law is a Calgary-based law firm dedicated to protecting your interests with skill, integrity, and compassion. We combine <Highlight>extensive legal expertise</Highlight> with a <Highlight>client-first approach</Highlight> to deliver effective solutions in a wide range of practice areas. Led by principal lawyer <Highlight>Dr. David Ike</Highlight>, Ph.D., our firm leverages global experience and local knowledge to provide personal attention and strategic counsel to every client. We believe in demystifying the law and <Highlight>empowering our clients</Highlight> – guiding you through each step with clarity, confidence, and unwavering advocacy.
          </Typography>
        </Box>

        {/* Practice Areas Section */}
        <Box sx={{ mb: 8 }}>
          <SectionTitle variant="h2" sx={{ mb: 5 }}>
            Our Practice Areas
          </SectionTitle>
          <PracticeAreasGrid />
        </Box>
      </Container>
      
      {/* CTA Section */}
      <CTASection>
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            Ready to Discuss Your Legal Needs?
          </Typography>
          <Typography variant="body1" sx={{ mb: 4 }}>
            Our team of experienced attorneys is here to help you navigate your legal challenges with confidence.
          </Typography>
          <Box>
            <PrimaryButton 
              variant="contained" 
              component={RouterLink} 
              to="/contact-us"
              sx={{ mr: 2 }}
            >
              Schedule Free Consultation
            </PrimaryButton>

          </Box>
        </Container>
      </CTASection>
    </>
  );
}

export default HomePage;